import { IDateQuestionValue } from '~/components/ClientExperience/OneFlow/FlowContent/QuestionBlock/MultiInputQuestions/DateQuestion/CXDateQuestion.types';
import dayjs from '../common/dayjs';
import {
    buildDateTimeStringsObjectForDate,
    dateStringToDateIgnoreTimezone,
    SHORT_DISPLAY_DATE_FORMAT,
    timeOptions
} from './date.utils';
import { trackError } from '~/services/logger/error.utils';

export type TShortTimezone =
    | 'PDT'
    | 'PST'
    | 'MDT'
    | 'MST'
    | 'EDT'
    | 'EST'
    | 'CDT'
    | 'CST'
    | 'HST'
    | 'UTC'
    | 'AKDT'
    | 'AKST'
    | 'HAST'
    | 'HADT'
    | 'AST'
    | 'ADT'
    | 'NST'
    | 'NDT'
    | 'GMT-7'
    | 'GMT-5'
    | 'GMT-4'
    | 'ART'
    | 'PYT'
    | 'COT'
    | 'AMT'
    | 'VET'
    | 'WGT'
    | 'UYT'
    | 'PMST'
    | 'FNT'
    | 'CLT'
    | 'BRT'
    | 'BOT'
    | 'EET'
    | 'CAT'
    | 'EAT'
    | 'WAT'
    | 'AZT'
    | 'BST'
    | 'GST'
    | 'IRKT'
    | 'ICT'
    | 'IDT'
    | 'AFT'
    | 'PKT'
    | 'IST'
    | 'KRAT'
    | 'OMST'
    | 'MMT'
    | 'IRST'
    | 'JST'
    | 'VLAT'
    | 'YAKT'
    | 'YEKT'
    | 'AZOT'
    | 'CVT'
    | 'WITA'
    | 'ACST'
    | 'AEST'
    | 'ACWST'
    | 'LHST'
    | 'AWST'
    | 'AEDT'
    | 'CEST'
    | 'GMT'
    | 'CET'
    | 'EEST'
    | 'FET'
    | 'MSK'
    | 'TRT'
    | 'WST'
    | 'NZDT'
    | 'CHADT'
    | 'FJT'
    | 'GAMT'
    | 'LINT'
    | 'MHT'
    | 'MART'
    | 'NFT'
    | 'NCT'
    | 'SST'
    | 'GILT'
    | 'TOT';

export type TMembersShortTimezone =
    | 'PDT'
    | 'MDT'
    | 'MST'
    | 'CDT'
    | 'CST'
    | 'EDT'
    | 'HST'
    | 'AKDT'
    | 'ADT'
    | 'AST'
    | 'NST';

export type TMembersTimezone =
    | 'America/Los_Angeles'
    | 'America/Denver'
    | 'America/Phoenix'
    | 'America/New_York'
    | 'America/Chicago'
    | 'America/Regina'
    | 'Pacific/Honolulu'
    | 'America/Anchorage'
    | 'America/Puerto_Rico'
    | 'America/Halifax'
    | 'America/St_Johns';

export type TFullTimezone =
    | 'America/Los_Angeles'
    | 'America/Denver'
    | 'America/Phoenix'
    | 'America/New_York'
    | 'America/Chicago'
    | 'America/Regina'
    | 'Pacific/Honolulu'
    | 'UTC'
    | 'America/Anchorage'
    | 'America/Puerto_Rico'
    | 'America/Halifax'
    | 'America/St_Johns'
    | 'America/Adak'
    | 'America/Argentina/Buenos_Aires'
    | 'America/Asuncion'
    | 'America/Bogota'
    | 'America/Campo_Grande'
    | 'America/Caracas'
    | 'America/Godthab'
    | 'America/Havana'
    | 'America/Mazatlan'
    | 'America/Mexico_City'
    | 'America/Montevideo'
    | 'America/Miquelon'
    | 'America/Noronha'
    | 'America/Santiago'
    | 'America/Santa_Isabel'
    | 'America/Sao_Paulo'
    | 'America/La_Paz'
    | 'America/Panama'
    | 'Africa/Cairo'
    | 'Africa/Windhoek'
    | 'Africa/Nairobi'
    | 'Africa/Harare'
    | 'Africa/Lagos'
    | 'Asia/Amman'
    | 'Asia/Baghdad'
    | 'Asia/Baku'
    | 'Asia/Beirut'
    | 'Asia/Damascus'
    | 'Asia/Dhaka'
    | 'Asia/Dubai'
    | 'Asia/Gaza'
    | 'Asia/Irkutsk'
    | 'Asia/Bangkok'
    | 'Asia/Jerusalem'
    | 'Asia/Kabul'
    | 'Asia/Karachi'
    | 'Asia/Kathmandu'
    | 'Asia/Kolkata'
    | 'Asia/Krasnoyarsk'
    | 'Asia/Omsk'
    | 'Asia/Yangon'
    | 'Asia/Shanghai'
    | 'Asia/Tehran'
    | 'Asia/Tokyo'
    | 'Asia/Vladivostok'
    | 'Asia/Yakutsk'
    | 'Asia/Yekaterinburg'
    | 'Asia/Makassar'
    | 'Asia/Yerevan'
    | 'Atlantic/Azores'
    | 'Atlantic/Cape_Verde'
    | 'Australia/Adelaide'
    | 'Australia/Brisbane'
    | 'Australia/Darwin'
    | 'Australia/Eucla'
    | 'Australia/Lord_Howe'
    | 'Australia/Perth'
    | 'Australia/Sydney'
    | 'Europe/Berlin'
    | 'Europe/Bucharest'
    | 'Europe/London'
    | 'Europe/Paris'
    | 'Europe/Athens'
    | 'Europe/Minsk'
    | 'Europe/Moscow'
    | 'Europe/Istanbul'
    | 'Pacific/Apia'
    | 'Pacific/Auckland'
    | 'Pacific/Chatham'
    | 'Pacific/Easter'
    | 'Pacific/Fiji'
    | 'Pacific/Gambier'
    | 'Pacific/Kiritimati'
    | 'Pacific/Majuro'
    | 'Pacific/Marquesas'
    | 'Pacific/Norfolk'
    | 'Pacific/Noumea'
    | 'Pacific/Pago_Pago'
    | 'Pacific/Pitcairn'
    | 'Pacific/Tarawa'
    | 'Pacific/Tongatapu';

// This dictionary is because of the way we handle timezones in the server.
export const shortTimezoneToFullTimezone = {
    // USA & CANADA
    PDT: 'America/Los_Angeles',
    PST: 'America/Los_Angeles',
    MDT: 'America/Denver',
    MST: 'America/Phoenix',
    EDT: 'America/New_York',
    EST: 'America/New_York',
    CDT: 'America/Chicago',
    CST: 'America/Regina',
    HST: 'Pacific/Honolulu',
    UTC: 'UTC',
    AKDT: 'America/Anchorage',
    AKST: 'America/Anchorage',
    HAST: 'Pacific/Honolulu',
    HADT: 'America/Anchorage',
    AST: 'America/Puerto_Rico',
    ADT: 'America/Halifax',
    NST: 'America/St_Johns',
    NDT: 'America/St_Johns',
    'GMT-7': 'America/Phoenix',
    'GMT-5': 'America/New_York',
    'GMT-4': 'America/New_York',
    ART: 'America/Argentina/Buenos_Aires',
    PYT: 'America/Asuncion',
    COT: 'America/Bogota',
    AMT: 'America/Campo_Grande',
    VET: 'America/Caracas',
    WGT: 'America/Godthab',
    UYT: 'America/Montevideo',
    PMST: 'America/Miquelon',
    FNT: 'America/Noronha',
    CLT: 'America/Santiago',
    BRT: 'America/Sao_Paulo',
    BOT: 'America/La_Paz',

    // Africa
    EET: 'Africa/Cairo',
    CAT: 'Africa/Windhoek',
    EAT: 'Africa/Nairobi',
    WAT: 'Africa/Lagos',

    // Asia
    AZT: 'Asia/Baku',
    BST: 'Asia/Dhaka',
    GST: 'Asia/Dubai',
    IRKT: 'Asia/Irkutsk',
    ICT: 'Asia/Bangkok',
    IDT: 'Asia/Jerusalem',
    AFT: 'Asia/Kabul',
    PKT: 'Asia/Karachi',
    IST: 'Asia/Kolkata',
    KRAT: 'Asia/Krasnoyarsk',
    OMST: 'Asia/Omsk',
    MMT: 'Asia/Yangon',
    IRST: 'Asia/Tehran',
    JST: 'Asia/Tokyo',
    VLAT: 'Asia/Vladivostok',
    YAKT: 'Asia/Yakutsk',
    WITA: 'Asia/Makassar',
    YEKT: 'Asia/Yekaterinburg',

    // Atlantic
    AZOT: 'Atlantic/Azores',
    CVT: 'Atlantic/Cape_Verde',

    // Australia
    ACST: 'Australia/Adelaide',
    AEST: 'Australia/Brisbane',
    ACWST: 'Australia/Eucla',
    LHST: 'Australia/Lord_Howe',
    AWST: 'Australia/Perth',
    AEDT: 'Australia/Sydney',

    // Europe
    CEST: 'Europe/Berlin',
    GMT: 'Europe/London',
    CET: 'Europe/Paris',
    EEST: 'Europe/Athens',
    FET: 'Europe/Minsk',
    MSK: 'Europe/Moscow',
    TRT: 'Europe/Istanbul',

    // Pacific
    WST: 'Pacific/Apia',
    NZDT: 'Pacific/Auckland',
    CHADT: 'Pacific/Chatham',
    FJT: 'Pacific/Fiji',
    GAMT: 'Pacific/Gambier',
    LINT: 'Pacific/Kiritimati',
    MHT: 'Pacific/Majuro',
    MART: 'Pacific/Marquesas',
    NFT: 'Pacific/Norfolk',
    NCT: 'Pacific/Noumea',
    SST: 'Pacific/Pago_Pago',
    GILT: 'Pacific/Tarawa',
    TOT: 'Pacific/Tongatapu'
} as const satisfies Record<TShortTimezone, TFullTimezone>;

export const fullTimezoneToShortTimezone = {
    'America/Los_Angeles': 'PDT',
    'America/Denver': 'MDT',
    'America/Phoenix': 'MST',
    'America/New_York': 'EDT',
    'America/Chicago': 'CDT',
    'America/Regina': 'CST',
    'Pacific/Honolulu': 'HST',
    UTC: 'UTC',
    'America/Anchorage': 'AKDT',
    'America/Puerto_Rico': 'AST',
    'America/Halifax': 'ADT',
    'America/St_Johns': 'NST',
    'America/Adak': 'HADT',
    'America/Argentina/Buenos_Aires': 'ART',
    'America/Asuncion': 'PYT',
    'America/Bogota': 'COT',
    'America/Campo_Grande': 'AMT',
    'America/Caracas': 'VET',
    'America/Godthab': 'WGT',
    'America/Havana': 'CDT',
    'America/Mazatlan': 'MST',
    'America/Mexico_City': 'CST',
    'America/Montevideo': 'UYT',
    'America/Miquelon': 'PMST',
    'America/Noronha': 'FNT',
    'America/Santiago': 'CLT',
    'America/Santa_Isabel': 'PST',
    'America/Sao_Paulo': 'BRT',
    'America/La_Paz': 'BOT',
    'America/Panama': 'EST',
    'Africa/Cairo': 'EET',
    'Africa/Windhoek': 'CAT',
    'Africa/Nairobi': 'EAT',
    'Africa/Harare': 'CAT',
    'Africa/Lagos': 'WAT',
    'Asia/Amman': 'EEST',
    'Asia/Baghdad': 'AST',
    'Asia/Baku': 'AZT',
    'Asia/Beirut': 'EEST',
    'Asia/Damascus': 'EEST',
    'Asia/Dhaka': 'BST',
    'Asia/Dubai': 'GST',
    'Asia/Gaza': 'EET',
    'Asia/Irkutsk': 'IRKT',
    'Asia/Bangkok': 'ICT',
    'Asia/Jerusalem': 'IDT',
    'Asia/Kabul': 'AFT',
    'Asia/Karachi': 'PKT',
    'Asia/Kathmandu': 'NST',
    'Asia/Kolkata': 'IST',
    'Asia/Krasnoyarsk': 'KRAT',
    'Asia/Omsk': 'OMST',
    'Asia/Yangon': 'MMT',
    'Asia/Shanghai': 'CST',
    'Asia/Tehran': 'IRST',
    'Asia/Tokyo': 'JST',
    'Asia/Vladivostok': 'VLAT',
    'Asia/Yakutsk': 'YAKT',
    'Asia/Yekaterinburg': 'YEKT',
    'Asia/Makassar': 'WITA',
    'Asia/Yerevan': 'AMT',
    'Atlantic/Azores': 'AZOT',
    'Atlantic/Cape_Verde': 'CVT',
    'Australia/Adelaide': 'ACST',
    'Australia/Brisbane': 'AEST',
    'Australia/Darwin': 'ACST',
    'Australia/Eucla': 'ACWST',
    'Australia/Lord_Howe': 'LHST',
    'Australia/Perth': 'AWST',
    'Australia/Sydney': 'AEDT',
    'Europe/Berlin': 'CEST',
    'Europe/Bucharest': 'EET',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Athens': 'EEST',
    'Europe/Minsk': 'FET',
    'Europe/Moscow': 'MSK',
    'Europe/Istanbul': 'TRT',
    'Pacific/Apia': 'WST',
    'Pacific/Auckland': 'NZDT',
    'Pacific/Chatham': 'CHADT',
    'Pacific/Easter': 'CLT',
    'Pacific/Fiji': 'FJT',
    'Pacific/Gambier': 'GAMT',
    'Pacific/Kiritimati': 'LINT',
    'Pacific/Majuro': 'MHT',
    'Pacific/Marquesas': 'MART',
    'Pacific/Norfolk': 'NFT',
    'Pacific/Noumea': 'NCT',
    'Pacific/Pago_Pago': 'SST',
    'Pacific/Pitcairn': 'PST',
    'Pacific/Tarawa': 'GILT',
    'Pacific/Tongatapu': 'TOT'
} as const satisfies Record<TFullTimezone, TShortTimezone>;

export const membersTimezoneToShortTimezone = {
    'America/Los_Angeles': 'PDT',
    'America/Denver': 'MDT',
    'America/Phoenix': 'MST',
    'America/New_York': 'EDT',
    'America/Chicago': 'CDT',
    'America/Regina': 'CST',
    'Pacific/Honolulu': 'HST',
    'America/Anchorage': 'AKDT',
    'America/Puerto_Rico': 'AST',
    'America/Halifax': 'ADT',
    'America/St_Johns': 'NST'
} as const satisfies Record<TMembersTimezone, TShortTimezone>;

const transformedTimezoneMapping: Record<TFullTimezone, string> = {
    'America/Los_Angeles': 'Pacific Time - US & Canada',
    'America/Denver': 'Mountain Time - US & Canada',
    'America/Chicago': 'Central Time - US & Canada',
    'America/Regina': 'Central Standard Time - US & Canada',
    'America/New_York': 'Eastern Time - US & Canada',
    'America/Halifax': 'Halifax Time',
    'America/Anchorage': 'Alaska Time',
    'America/Phoenix': 'Arizona Time',
    'America/St_Johns': 'Newfoundland Time',
    'Pacific/Honolulu': 'Hawaii Time',

    'America/Adak': 'America/Adak',
    'America/Argentina/Buenos_Aires': 'Buenos Aires Time',
    'America/Asuncion': 'Asuncion Time',
    'America/Bogota': 'Bogota, Jamaica, Lima Time',
    'America/Campo_Grande': 'America/Campo Grande',
    'America/Caracas': 'Caracas Time',
    'America/Godthab': 'America/Godthab',
    'America/Havana': 'America/Havana',
    'America/Mazatlan': 'America/Mazatlan',
    'America/Mexico_City': 'Mexico City Time',
    'America/Montevideo': 'Montevideo Time',
    'America/Miquelon': 'America/Miquelon',
    'America/Noronha': 'America/Noronha',
    'America/Santiago': 'Santiago Time',
    'America/Santa_Isabel': 'America/Santa Isabel',
    'America/Puerto_Rico': 'Atlantic Standard Time',
    'America/Sao_Paulo': 'Brasilia Time',
    'America/La_Paz': 'America/La_Paz',
    'America/Panama': 'America/Panama',

    'Africa/Cairo': 'Africa/Cairo',
    'Africa/Windhoek': 'Africa/Windhoek',
    'Africa/Nairobi': 'Africa/Nairobi',
    'Africa/Harare': 'Central Africa Time',
    'Africa/Lagos': 'West Africa Time',

    'Asia/Amman': 'Jordan Time',
    'Asia/Baghdad': 'Baghdad, East Africa Time',
    'Asia/Baku': 'Asia/Baku',
    'Asia/Beirut': 'Lebanon Time',
    'Asia/Damascus': 'Syria Time',
    'Asia/Dhaka': 'Asia/Dhaka',
    'Asia/Dubai': 'Dubai Time',
    'Asia/Gaza': 'Asia/Gaza',
    'Asia/Irkutsk': 'Asia/Irkutsk',
    'Asia/Bangkok': 'Indochina Time',
    'Asia/Jerusalem': 'Israel Time',
    'Asia/Kabul': 'Kabul Time',
    'Asia/Karachi': 'Pakistan, Maldives Time',
    'Asia/Kathmandu': 'Kathmandu Time',
    'Asia/Kolkata': 'India, Sri Lanka Time',
    'Asia/Krasnoyarsk': 'Krasnoyarsk Time',
    'Asia/Omsk': 'Asia/Omsk',
    'Asia/Yangon': 'Asia/Rangoon',
    'Asia/Shanghai': 'China, Singapore, Perth',
    'Asia/Tehran': 'Tehran Time',
    'Asia/Tokyo': 'Japan, Korea Time',
    'Asia/Vladivostok': 'Asia/Vladivostok',
    'Asia/Yakutsk': 'Asia/Yakutsk',
    'Asia/Makassar': 'Asia/Makassar',
    'Asia/Yekaterinburg': 'Yekaterinburg Time',
    'Asia/Yerevan': 'Asia/Yerevan',

    'Atlantic/Azores': 'Azores Time',
    'Atlantic/Cape_Verde': 'Cape Verde Time',

    'Australia/Adelaide': 'Adelaide Time',
    'Australia/Brisbane': 'Brisbane Time',
    'Australia/Darwin': 'Australia/Darwin',
    'Australia/Eucla': 'Australia/Eucla',
    'Australia/Lord_Howe': 'Australia/Lord Howe',
    'Australia/Perth': 'Australia/Perth',
    'Australia/Sydney': 'Sydney, Melbourne Time',

    UTC: 'UTC Time',

    'Europe/Berlin': 'Central European Time',
    'Europe/Bucharest': 'Eastern European Time',
    'Europe/London': 'Uk, Ireland, Lisbon Time',
    'Europe/Paris': 'Europe/Paris',
    'Europe/Athens': 'Europe/Athens',
    'Europe/Minsk': 'Minsk Time',
    'Europe/Moscow': 'Moscow Time',
    'Europe/Istanbul': 'Turkey Time',

    'Pacific/Apia': 'Pacific/Apia',
    'Pacific/Auckland': 'Auckland Time',
    'Pacific/Chatham': 'Pacific/Chatham',
    'Pacific/Easter': 'Pacific/Easter',
    'Pacific/Fiji': 'Pacific/Fiji',
    'Pacific/Gambier': 'Pacific/Gambier',
    'Pacific/Kiritimati': 'Pacific/Kiritimati',
    'Pacific/Majuro': 'Pacific/Majuro',
    'Pacific/Marquesas': 'Pacific/Marquesas',
    'Pacific/Norfolk': 'Pacific/Norfolk',
    'Pacific/Noumea': 'Pacific/Noumea',
    'Pacific/Pago_Pago': 'Pacific/Pago Pago',
    'Pacific/Pitcairn': 'Pacific/Pitcairn',
    'Pacific/Tarawa': 'Pacific/Tarawa',
    'Pacific/Tongatapu': 'Pacific/Tongatapu'
};

const transformedMembersTimezoneMapping: Record<TMembersTimezone, string> = {
    'America/Los_Angeles': 'Pacific Time - US & Canada',
    'America/Denver': 'Mountain Time - US & Canada',
    'America/Phoenix': 'Arizona Time',
    'America/New_York': 'Eastern Time - US & Canada',
    'America/Chicago': 'Central Time - US & Canada',
    'America/Regina': 'Central Standard Time - US & Canada',
    'Pacific/Honolulu': 'Hawaii Time',
    'America/Anchorage': 'Alaska Time',
    'America/Puerto_Rico': 'Atlantic Standard Time',
    'America/Halifax': 'Halifax Time',
    'America/St_Johns': 'Newfoundland Time'
};

export const memberServerToDisplayTimezonesMapping: Record<
    TMembersShortTimezone,
    string
> = {
    PDT: 'PDT/PST',
    MDT: 'MDT/MST',
    MST: 'MST',
    CDT: 'CDT/CST',
    CST: 'CST',
    EDT: 'EDT/EST',
    HST: 'HST',
    AKDT: 'AKDT/AKST',
    ADT: 'ADT/AST',
    AST: 'AST',
    NST: 'NST/NDT'
};

export const timezonesAsDropdownOptions: Array<{
    value: TFullTimezone;
    label: string;
}> = Object.entries(transformedTimezoneMapping).map(
    (element: [TFullTimezone, string]) => {
        return {
            value: element[0],
            label: element[1]
        };
    }
);

export const timezonesAsDropdownOptionsShort = timezonesAsDropdownOptions.map(
    ({ label, value }) => ({
        label,
        value: fullTimezoneToShortTimezone[value]
    })
);

export const membersTimezonesAsDropdownOptions: Array<{
    value: TFullTimezone;
    label: string;
}> = Object.entries(transformedMembersTimezoneMapping).map(
    (element: [TFullTimezone, string]) => {
        return {
            value: element[0],
            label: element[1]
        };
    }
);

export const membersTimezonesAsDropdownOptionsShort =
    membersTimezonesAsDropdownOptions.map(({ label, value }) => ({
        label,
        value: fullTimezoneToShortTimezone[value]
    }));

export const membersTimezoneWithShortLabelsAsDropdownOptionsShort =
    membersTimezonesAsDropdownOptions.map(({ value }) => ({
        label: memberServerToDisplayTimezonesMapping[
            fullTimezoneToShortTimezone[value] as TMembersShortTimezone
        ],
        value: fullTimezoneToShortTimezone[value]
    }));

function isFullTimezone(timezone: string): timezone is TFullTimezone {
    return timezone in fullTimezoneToShortTimezone;
}

function isMembersTimezone(timezone: string): timezone is TFullTimezone {
    return timezone in membersTimezoneToShortTimezone;
}

function isShortTimezone(timezone: string): timezone is TShortTimezone {
    return timezone in shortTimezoneToFullTimezone;
}

/** get user's timezone, in order to handle daylight savings, if needed to be used on future/past date, send date as param. */
export function getUserTimezone(futureDate: Date = new Date()): TFullTimezone {
    const fullTimezone = dayjs.tz.guess();
    // full time zone is already handling DST, so no need to pass futureDate
    if (isFullTimezone(fullTimezone)) {
        return fullTimezone;
    }

    const shortTimezone = dayjs(futureDate).tz(fullTimezone).format('z');

    if (isShortTimezone(shortTimezone)) {
        return shortTimezoneToFullTimezone[shortTimezone];
    }
}

export function getUserTimezoneWithFallback() {
    return getUserTimezone() || shortTimezoneToFullTimezone.PDT;
}

export function getMembersTimezoneWithFallback() {
    const userTimeZone = getUserTimezone();

    return isMembersTimezone(userTimeZone)
        ? userTimeZone
        : shortTimezoneToFullTimezone.PDT;
}

export function getUserMemberDisplayTimezone(): string | null {
    const userTimeZone = getUserTimezone();

    if (!isMembersTimezone(userTimeZone)) {
        return null;
    }

    return memberServerToDisplayTimezonesMapping[
        membersTimezoneToShortTimezone[userTimeZone]
    ];
}

export function getTimezonedDate(date: string, timezone: TFullTimezone) {
    return dayjs.utc(date).tz(timezone);
}

export function formatDateWithTimezone(
    date: string,
    fullTimezone: TFullTimezone
): string {
    const shortTimezone = fullTimezoneToShortTimezone[fullTimezone];
    try {
        return getTimezonedDate(date, fullTimezone).format(
            `ddd, MMM D, YYYY [|] h:mm A [(${shortTimezone})]`
        );
    } catch (error) {
        trackError({
            error: new Error('Error in formatDate: getTimezonedDate'),
            context: {
                date,
                fullTimezone,
                error
            }
        });
    }
}

export function convertToUTCDateTimeStrings(
    date: string,
    time: string,
    dateFormat: string = SHORT_DISPLAY_DATE_FORMAT
) {
    if (!date || !time) {
        return { date, time };
    }

    const minutes = timeOptions.findIndex(x => x.value === time) * 15;

    const dateInUtc = dayjs.utc(date).add(minutes, 'm');

    return buildDateTimeStringsObjectForDate(dateInUtc, dateFormat);
}

export function convertDateAndTimeStringsToTimezoneDate(
    date: string,
    time: string,
    timezone: string
) {
    if (!date) {
        return null;
    }

    const minutes = timeOptions.findIndex(x => x.value === time) * 15;
    const dayInUTC = dayjs.utc(date);
    let dateInTimezone = dayInUTC.tz(
        shortTimezoneToFullTimezone[timezone] || timezone
    );
    if (time) {
        dateInTimezone = dateInTimezone.add(minutes, 'm');
    }

    return dateInTimezone;
}

export function convertToTimezoneDateTimeStrings(
    date: string,
    time: string,
    timezone: string
) {
    if (!date || !time) {
        return { date, time };
    }

    const dateInTimezone = convertDateAndTimeStringsToTimezoneDate(
        date,
        time,
        timezone
    );

    return buildDateTimeStringsObjectForDate(dateInTimezone);
}

export function convertDateTimeToCurrentTimezone(
    value: IDateQuestionValue,
    timezone: string
): IDateQuestionValue {
    if (!value.start_time) {
        // format dates without changing timezone
        const newStartDate = dateStringToDateIgnoreTimezone(value.start_date);
        const newEndDate = dateStringToDateIgnoreTimezone(value.end_date);
        return {
            ...value,
            start_date: newStartDate
                ? dayjs(newStartDate).format(SHORT_DISPLAY_DATE_FORMAT)
                : undefined,
            end_date: newEndDate
                ? dayjs(newEndDate).format(SHORT_DISPLAY_DATE_FORMAT)
                : undefined
        };
    }

    const _timezone = timezone || dayjs.tz.guess();

    const { date: start_date, time: start_time } =
        convertToTimezoneDateTimeStrings(
            value.start_date,
            value.start_time,
            _timezone
        );

    const { date: end_date, time: end_time } = convertToTimezoneDateTimeStrings(
        value.end_date,
        value.end_time,
        _timezone
    );

    return {
        start_date,
        start_time,
        end_date,
        end_time
    };
}

export function convertDateToSpecificTimezone(
    date: Date,
    timezone: string = shortTimezoneToFullTimezone.PDT
) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Months are 0-indexed (0 = January)
    const day = date.getDate();

    return dayjs.tz(`${year}-${month}-${day}`, timezone).toDate();
}
